/**
 * Phase R3 — flags locaux par thread : "À traiter" (later) et "En attente"
 * (snoozed jusqu'à un timestamp). Stockés en IndexedDB pour persister
 * cross-session mais NON synchronisés avec Gmail (= factice v1).
 *
 * Évolution future : remplacer par labels Gmail `_later` / `_snoozed_<ts>`
 * + un cron Worker qui re-pose `INBOX` quand le snooze expire. Pour
 * l'instant l'app maintient juste un Map { threadId → flags } en mémoire,
 * persisté à chaque mutation.
 *
 * Le filtre `gmailState.filter` lit cette state au moment du render pour
 * filtrer les rows : "À traiter" affiche les threads avec `later=true`,
 * "En attente" affiche les threads avec `snoozed > now`.
 */

import { get, set } from "idb-keyval";

const STORE_KEY = "mailfox:thread-flags:v1";

export interface ThreadFlags {
  /** True si l'user a marqué "À traiter" (Inbox Zero). */
  later?: boolean;
  /** Unix ms — thread caché jusqu'à cette date. null/0 = pas snooze. */
  snoozedUntil?: number | null;
}

interface FlagsState {
  /** Map threadId → flags. Lecture O(1) pour les filtres. */
  byId: Record<string, ThreadFlags>;
  loaded: boolean;
}

export const flagsState = $state<FlagsState>({
  byId: {},
  loaded: false,
});

/** Charge depuis IDB au boot. Idempotent. */
export async function loadThreadFlags(): Promise<void> {
  if (flagsState.loaded) return;
  try {
    const v = await get<Record<string, ThreadFlags>>(STORE_KEY);
    flagsState.byId = v ?? {};
  } catch (e) {
    console.warn("loadThreadFlags failed:", e);
    flagsState.byId = {};
  }
  flagsState.loaded = true;
}

async function persist(): Promise<void> {
  try {
    await set(STORE_KEY, flagsState.byId);
  } catch (e) {
    console.warn("persist threadFlags failed:", e);
  }
}

export async function toggleLater(threadId: string): Promise<void> {
  const cur = flagsState.byId[threadId] ?? {};
  flagsState.byId = {
    ...flagsState.byId,
    [threadId]: { ...cur, later: !cur.later },
  };
  await persist();
}

export async function setSnooze(threadId: string, until: number | null): Promise<void> {
  const cur = flagsState.byId[threadId] ?? {};
  flagsState.byId = {
    ...flagsState.byId,
    [threadId]: { ...cur, snoozedUntil: until },
  };
  await persist();
}

/** True si snooze actif (= timestamp futur). */
export function isSnoozed(threadId: string): boolean {
  const f = flagsState.byId[threadId];
  if (!f?.snoozedUntil) return false;
  return f.snoozedUntil > Date.now();
}

export function isLater(threadId: string): boolean {
  return flagsState.byId[threadId]?.later === true;
}
